/**
 * RequireAuth — route guard that redirects unauthenticated users to /login.
 *
 * Usage in App.tsx:
 *   <Route element={<RequireAuth />}>
 *     <Route path="/dashboards" element={<Dashboards />} />
 *     ...other protected routes...
 *   </Route>
 *
 * The <Outlet /> renders the matched child route. While auth is loading
 * (first /api/auth/me call hasn't returned), we show a minimal placeholder
 * to avoid flashing the login page or partially-rendered protected content.
 */
import { Navigate, Outlet } from "react-router-dom";
import { useAuth } from "./AuthContext";

export function RequireAuth() {
  const { status } = useAuth();

  if (status === "loading") {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ background: "var(--bg)" }}
      >
        <div
          className="font-serif italic text-[14.5px]"
          style={{ color: "var(--ink-3)" }}
        >
          Loading…
        </div>
      </div>
    );
  }

  if (status === "unauthenticated") {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

/**
 * RequireAdmin — like RequireAuth but additionally checks the admin role.
 *
 * Non-admin users get sent home (/dashboards) rather than a "Forbidden" page —
 * they don't need to see the admin URL exists.
 */
export function RequireAdmin() {
  const { status, user } = useAuth();

  if (status === "loading") {
    return null;
  }

  if (status === "unauthenticated") {
    return <Navigate to="/login" replace />;
  }

  if (!user?.is_admin) {
    return <Navigate to="/dashboards" replace />;
  }

  return <Outlet />;
}

/**
 * RequireAIAnalyst — guards the /ai route.
 *
 * Users without explicit AI Analyst access get sent to /dashboards. This is
 * independent of admin status — admins do NOT automatically have AI Analyst
 * access (opt-in model). An admin must be added to the ai_analyst Cognito
 * group, same as any other user, to use the feature.
 *
 * Mirror of RequireAdmin's silent-redirect behavior: users without access
 * are sent home rather than shown a "Forbidden" page, so the existence of
 * the feature isn't advertised.
 */
export function RequireAIAnalyst() {
  const { status, user } = useAuth();

  if (status === "loading") {
    return null;
  }

  if (status === "unauthenticated") {
    return <Navigate to="/login" replace />;
  }

  if (!user?.has_ai_analyst) {
    return <Navigate to="/dashboards" replace />;
  }

  return <Outlet />;
}


/**
 * RequireAssumptions — guards the /data route (Assumptions Hub).
 *
 * Users without explicit assumptions access get sent to /dashboards. Like
 * AI Analyst, this is independent of admin status (opt-in model) and uses a
 * silent redirect rather than a "Forbidden" page, so the feature isn't
 * advertised to users who don't have it.
 */
export function RequireAssumptions() {
  const { status, user } = useAuth();

  if (status === "loading") {
    return null;
  }

  if (status === "unauthenticated") {
    return <Navigate to="/login" replace />;
  }

  if (!user?.has_assumptions) {
    return <Navigate to="/dashboards" replace />;
  }

  return <Outlet />;
}


/**
 * RequireEventViewer — guards the /events/* routes (Event Reporting).
 *
 * Passes for event_viewer or event_editor. Same silent-redirect, opt-in model
 * as RequireAssumptions. This is a UX gate only: the API (require_event_*)
 * and Supabase RLS each enforce access independently.
 */
export function RequireEventViewer() {
  const { status, user } = useAuth();

  if (status === "loading") {
    return null;
  }

  if (status === "unauthenticated") {
    return <Navigate to="/login" replace />;
  }

  if (!user?.has_event_viewer) {
    return <Navigate to="/dashboards" replace />;
  }

  return <Outlet />;
}
